import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import {
  Page,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { SourceEditModal } from "@/pages/settings/recruit/sources/widgets/SourceEditModal.tsx";
import { useSourceDelete, useSources } from "@/queries/use-sources.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiSource, SourceTypes } from "@/types/recruit/source.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export default function Sources() {
  const { t } = useTranslation();

  useTitle(t("ats:sources"), t("common:settings"));

  const modalsService = useModals();
  const { data: sources, isLoading, isFetching } = useSources();
  const sourceDelete = useSourceDelete();

  const handleEditOrCreate = useCallback((source: ApiSource | undefined = undefined) => {
    const sourceToEdit = source || {
      id: 0,
      title: "",
      source_type: SourceTypes.custom,
      datetime_created_at: new Date(),
    };
    modalsService.openModal(SourceEditModal, {
      source: sourceToEdit,
    });
  }, []);

  const handleDelete = useCallback((source: ApiSource) => {
    sourceDelete.mutate(source.id, {
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.sources.deleted, {
          [analyticProperties.id]: source.id,
        });
      },
    });
  }, []);

  const columns = useMemo<ColumnDef<ApiSource>[]>(
    () => [
      {
        header: t("common:title"),
        cell: (row) => <TableCell>{row.row.original.title}</TableCell>,
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
    ],
    [sources]
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.sources.viewed);
  });

  const renderRowAction = useCallback(
    (row: Row<ApiSource>) =>
      row.original.source_type === SourceTypes.custom ? (
        <DropdownMenu>
          <DropdownMenuItem
            onClick={() => {
              handleEditOrCreate(row.original);
            }}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
            title={t("common:edit")}
          />
          <DropdownMenuItem
            onClick={() => {
              handleDelete(row.original);
            }}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
            variant={"menuDanger"}
            title={t("common:delete") + "..."}
          />
        </DropdownMenu>
      ) : null,
    [handleDelete, handleEditOrCreate, t]
  );

  return (
    <>
      <Page>
        <Page.Header
          showBack={true}
          title={t("ats:sources")}
          navigateTo={"/settings/recruitment-directories"}
        >
          <Stack>
            <Button
              onClick={() => {
                handleEditOrCreate();
              }}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            >
              {t("common:create_new")}
            </Button>
          </Stack>
        </Page.Header>
        <Page.Content>
          <Table
            renderRowAction={renderRowAction}
            data={sources || []}
            columns={columns}
            showSkeleton={isLoading}
            isFetching={isFetching || sourceDelete.isPending}
            notFoundComponent={<EmptyStateNothingFound />}
            emptyStateComponent={<EmptyState />}
          />
        </Page.Content>
      </Page>
    </>
  );
}
